import { Badge, Button, Select, Skeleton, StatCard, Surface } from '@chopchop-se/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StocktakeLine } from '@warehouse/shared';
import { CheckCheck, ChevronLeft, ListChecks, PlayCircle, Tablet, TriangleAlert, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { STOCKTAKE_TONE } from './StocktakingPage';
import { useAuthStore, userHasPermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * Stocktake detail — plan, watch the count come in live, review the diffs and
 * POST them.
 *
 * Posting is the whole point of this module: the old WMS closed a stocktake by
 * flipping `active = false` and never touched stock
 * (`docs/recon/bc-inventory-stocktaking.md`). Here the confirm dialog says in
 * plain Swedish how many lines will move the balance, and the button is gated
 * on `warehouse.inventory.adjust`.
 */
export function StocktakeDetailPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const canPlan = userHasPermission(user, permissions, ['warehouse.inventory.task']);
  const canPost = userHasPermission(user, permissions, ['warehouse.inventory.adjust']);

  const { state, setState, setFilter } = useListState({ defaultSort: 'binCode', defaultDir: 'asc' });

  const stocktake = useQuery({
    queryKey: warehouseKeys.stocktake(id),
    queryFn: () => warehouseApi.getStocktake(id),
    enabled: id.length > 0,
  });
  const lines = useQuery({
    queryKey: warehouseKeys.stocktakeLines(id, listQueryKey(state)),
    queryFn: () => warehouseApi.listStocktakeLines(id, state),
    enabled: id.length > 0,
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['stocktakes'], warehouseKeys.stocktake(id));
  useLiveQuery(['stocktakes'], warehouseKeys.stocktakeLines(id));

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
  };

  const start = useMutation({
    mutationFn: () => warehouseApi.startStocktake(id),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:stocktaking.started'));
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const review = useMutation({
    mutationFn: () => warehouseApi.reviewStocktake(id),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:stocktaking.reviewed'));
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const post = useMutation({
    mutationFn: () => warehouseApi.postStocktake(id, { stocktakeId: id, treatUncountedAsZero: false }),
    onSuccess: (result) => {
      toastSuccess(
        result.adjustedLines > 0
          ? t('warehouseCore:stocktaking.posted', { count: result.adjustedLines })
          : t('warehouseCore:stocktaking.postedNoDiff'),
      );
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const remove = useMutation({
    mutationFn: () => warehouseApi.deleteStocktake(id),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:stocktaking.deleted'));
      invalidate();
      void navigate('/warehouse/stocktaking');
    },
    onError: (error) => {
      toastError(error);
    },
  });

  if (stocktake.isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-64 rounded" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }
  if (stocktake.error || !stocktake.data) {
    return (
      <Surface variant="subtle" className="p-6 text-sm text-ink-muted">
        {t('warehouseCore:stocktaking.empty')}
      </Surface>
    );
  }

  const take = stocktake.data;

  const columns: DataTableColumn<StocktakeLine>[] = [
    {
      id: 'bin',
      header: t('warehouseCore:stocktaking.bin'),
      sortField: 'binCode',
      cell: (row) => <span className="font-mono text-sm">{row.binCode}</span>,
    },
    {
      id: 'article',
      header: t('warehouseCore:stocktaking.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.articleName}</p>
          <p className="font-mono text-xs text-ink-muted">{row.articleNo}</p>
        </div>
      ),
    },
    {
      id: 'lot',
      header: t('warehouseCore:stocktaking.lot'),
      cell: (row) => row.lotNo ?? '—',
      hideBelow: 'lg',
    },
    {
      id: 'expected',
      header: t('warehouseCore:stocktaking.expected'),
      align: 'right',
      sortField: 'expectedQtyBase',
      cell: (row) => <span className="font-mono">{formatQty(row.expectedQtyBase)}</span>,
    },
    {
      id: 'counted',
      header: t('warehouseCore:stocktaking.countedQty'),
      align: 'right',
      sortField: 'countedQtyBase',
      cell: (row) =>
        row.countedQtyBase === null ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <span className="font-mono">{formatQty(row.countedQtyBase)}</span>
        ),
    },
    {
      id: 'diff',
      header: t('warehouseCore:stocktaking.diff'),
      align: 'right',
      sortField: 'diffQtyBase',
      cell: (row) => <DiffCell diff={row.diffQtyBase} />,
    },
    {
      id: 'status',
      header: t('warehouseCore:stocktaking.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={row.status === 'approved' ? 'success' : row.status === 'counted' ? 'brand' : 'neutral'}>
          {t(`warehouseCore:stocktaking.lineStatuses.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'countedBy',
      header: t('warehouseCore:stocktaking.countedBy'),
      cell: (row) => row.countedByName ?? '—',
      hideBelow: 'xl',
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader
        breadcrumb={
          <Link to="/warehouse/stocktaking" className="inline-flex items-center gap-1 hover:text-ink">
            <ChevronLeft className="size-3.5" aria-hidden />
            {t('warehouseCore:stocktaking.title')}
          </Link>
        }
        title={take.name}
        subtitle={
          take.postedAt
            ? `${t('warehouseCore:stocktaking.postedAt')}: ${formatDateTime(take.postedAt)}`
            : `${t('warehouseCore:stocktaking.createdAt')}: ${formatDateTime(take.createdAt)}`
        }
        actions={
          <>
            <Badge tone={STOCKTAKE_TONE[take.status]}>
              {t(`warehouseCore:stocktaking.statuses.${take.status}`)}
            </Badge>

            {take.status === 'counting' || take.status === 'review' ? (
              <Link to={`/stocktaking/${take.id}`}>
                <Button variant="secondary" size="sm" icon={<Tablet />}>
                  {t('warehouseCore:stocktaking.openCounting')}
                </Button>
              </Link>
            ) : null}

            {canPlan && take.status === 'draft' ? (
              <Button
                variant="primary"
                size="sm"
                icon={<PlayCircle />}
                loading={start.isPending}
                onClick={() => {
                  start.mutate();
                }}
              >
                {t('warehouseCore:stocktaking.start')}
              </Button>
            ) : null}

            {canPlan && take.status === 'counting' ? (
              <Button
                variant="secondary"
                size="sm"
                loading={review.isPending}
                onClick={() => {
                  review.mutate();
                }}
              >
                {t('warehouseCore:stocktaking.review')}
              </Button>
            ) : null}

            {canPost && (take.status === 'counting' || take.status === 'review') ? (
              <Button
                variant="primary"
                size="sm"
                icon={<CheckCheck />}
                loading={post.isPending}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: t('warehouseCore:stocktaking.postConfirmTitle'),
                      message: t('warehouseCore:stocktaking.postConfirmMessage', { count: take.diffCount }),
                      confirmLabel: t('warehouseCore:stocktaking.post'),
                      variant: 'warning',
                    });
                    if (ok) post.mutate();
                  })();
                }}
              >
                {t('warehouseCore:stocktaking.post')}
              </Button>
            ) : null}

            {canPlan && take.status !== 'posted' ? (
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 />}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: t('warehouseCore:stocktaking.delete'),
                      message: t('warehouseCore:stocktaking.deleteConfirm', { name: take.name }),
                      confirmLabel: t('common:actions.delete'),
                      variant: 'danger',
                    });
                    if (ok) remove.mutate();
                  })();
                }}
              >
                {t('common:actions.delete')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={<ListChecks />}
          label={t('warehouseCore:stocktaking.lines')}
          value={take.lineCount}
        />
        <StatCard
          icon={<CheckCheck />}
          label={t('warehouseCore:stocktaking.counted')}
          value={`${take.countedCount}/${take.lineCount}`}
        />
        <StatCard
          icon={<TriangleAlert />}
          label={t('warehouseCore:stocktaking.diffs')}
          value={take.diffCount}
        />
      </div>

      <DataTable
        columns={columns}
        rows={lines.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={lines.data?.total ?? null}
        loading={lines.isFetching}
        error={lines.error}
        dense
        emptyTitle={t('warehouseCore:stocktaking.noLines')}
        emptyDescription={t('warehouseCore:stocktaking.noLinesHelp')}
        filters={
          <>
            <Select
              aria-label={t('warehouseCore:stocktaking.onlyDiffs')}
              className="w-48"
              value={state.filters?.hasDiff === true ? 'diff' : ''}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'diff', label: t('warehouseCore:stocktaking.onlyDiffs') },
              ]}
              onChange={(event) => {
                setFilter('hasDiff', event.target.value === 'diff' ? true : undefined);
              }}
            />
            <Select
              aria-label={t('warehouseCore:stocktaking.onlyUncounted')}
              className="w-48"
              value={String(state.filters?.status ?? '')}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'open', label: t('warehouseCore:stocktaking.onlyUncounted') },
                { value: 'counted', label: t('warehouseCore:stocktaking.lineStatuses.counted') },
                { value: 'approved', label: t('warehouseCore:stocktaking.lineStatuses.approved') },
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
          </>
        }
      />
    </>
  );
}

/** A diff column that reads at a glance: red short, green over, grey exact. */
export function DiffCell({ diff }: { diff: string | null }): ReactNode {
  if (diff === null) return <span className="text-ink-muted">—</span>;
  const value = Number(diff);
  if (value === 0) return <span className="font-mono text-ink-muted">0</span>;
  return (
    <span className={`font-mono font-medium ${value < 0 ? 'text-danger-600' : 'text-emerald-600'}`}>
      {value > 0 ? '+' : ''}
      {formatQty(diff)}
    </span>
  );
}
